/**
 * Synthetic Web Audio sounds. No external assets. Subtle by design.
 * open(): soft mechanical click + swoosh; used when a crate starts opening
 * reveal(rarity): ascending chime; louder / more sparkly for higher rarity
 */
let _ctx = null;
function ctx() {
  if (!_ctx) {
    try { _ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch { _ctx = null; }
  }
  if (_ctx && _ctx.state === "suspended") _ctx.resume().catch(() => {});
  return _ctx;
}

function tone({ freq = 440, type = "sine", start = 0, dur = 0.2, gain = 0.15, freqTo = null, filter = null }) {
  const ac = ctx(); if (!ac) return;
  const t0 = ac.currentTime + start;
  const osc = ac.createOscillator();
  const g = ac.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  if (freqTo != null) osc.frequency.exponentialRampToValueAtTime(Math.max(1, freqTo), t0 + dur);
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  let node = osc;
  if (filter) {
    const bp = ac.createBiquadFilter();
    bp.type = filter.type || "bandpass";
    bp.frequency.value = filter.freq || freq;
    bp.Q.value = filter.q || 1;
    node.connect(bp); node = bp;
  }
  node.connect(g).connect(ac.destination);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

function noise({ start = 0, dur = 0.25, gain = 0.06, freq = 800, q = 8 }) {
  const ac = ctx(); if (!ac) return;
  const bufSize = Math.max(1, Math.floor(ac.sampleRate * dur));
  const buf = ac.createBuffer(1, bufSize, ac.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < bufSize; i++) data[i] = Math.random() * 2 - 1;
  const src = ac.createBufferSource();
  src.buffer = buf;
  const bp = ac.createBiquadFilter();
  bp.type = "bandpass"; bp.frequency.value = freq; bp.Q.value = q;
  const g = ac.createGain();
  const t0 = ac.currentTime + start;
  g.gain.setValueAtTime(0, t0);
  g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(bp).connect(g).connect(ac.destination);
  src.start(t0); src.stop(t0 + dur + 0.05);
}

export function playOpen() {
  // Soft mechanical click + brief low swoosh
  tone({ freq: 220, freqTo: 90, type: "triangle", dur: 0.25, gain: 0.08 });
  noise({ dur: 0.22, freq: 900, q: 4, gain: 0.05 });
  tone({ freq: 640, type: "square", dur: 0.05, gain: 0.03, start: 0.02 });
}

const CHORDS = {
  common:    [523, 659],                     // C5, E5
  rare:      [523, 659, 784],                // C5, E5, G5
  epic:      [523, 659, 784, 988],           // + B5
  legendary: [523, 659, 784, 988, 1175],     // + D6
};

export function playReveal(rarity = "common") {
  const notes = CHORDS[rarity] || CHORDS.common;
  const gain = rarity === "legendary" ? 0.14 : rarity === "epic" ? 0.11 : 0.09;
  notes.forEach((f, i) => {
    tone({ freq: f, type: "sine", start: i * 0.06, dur: 0.55, gain });
    tone({ freq: f * 2, type: "sine", start: i * 0.06, dur: 0.4, gain: gain * 0.35 });
  });
  // sparkle noise for higher rarities
  if (rarity === "epic" || rarity === "legendary") {
    noise({ start: 0.05, dur: 0.5, freq: 4000, q: 12, gain: 0.03 });
    noise({ start: 0.15, dur: 0.35, freq: 6000, q: 14, gain: 0.025 });
  }
}

// Short tick used by the fortune-wheel reveal for each item that passes the pointer.
export function playTick(strength = 1) {
  tone({ freq: 1400, type: "square", dur: 0.03, gain: 0.05 * strength });
  noise({ dur: 0.02, freq: 3200, q: 20, gain: 0.02 * strength });
}
